/*
 *  Copyright (c) 2024 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Config } from "../config";
import { makeRequest } from "../common/index";
import qs from 'qs'

const base_url = Config.admin.api_base_url;
const context = Config.admin.context;
const endpoint = `/${context}/keyword`;

/**
 * Create a new keyword for a specific reference.
 * @param {string} token - The Bearer token for authorization.
 * @param {Object} payload - The request payload.
 * @param {string} payload.guid - The UUID associated with the resource.
 * @param {string[]} payload.keywords - An array of keywords (strings, max 128 characters).
 * @param {string} payload.ref_type - The type of the reference.
 * @param {string} [payload.ref_subtype] - The optional subtype of the reference.
 * @returns {Promise<Object>} - A promise that resolves to the response from the API.
 */
const createKeyword = async (token, payload) => {
    const method = "POST";
    const path = `${endpoint}`;
    const headers = { Authorization: `Bearer ${token}` };
    return await makeRequest(method, base_url, path, headers, payload);
};

/**
 * Fetch all keywords.
 * @param {string} token - The Bearer token for authorization.
 * @returns {Promise<Object>} - A promise that resolves to the list of all keywords.
 */
const getAllKeywords = async (token) => {
    const method = "GET";
    const path = `${endpoint}`;
    const headers = { Authorization: `Bearer ${token}` };
    const payload = undefined;
    return await makeRequest(method, base_url, path, headers, payload);
};

/**
 * Fetch keywords associated with a specific GUID.
 * @param {string} token - The Bearer token for authorization.
 * @param {string} guid - The UUID associated with the resource.
 * @returns {Promise<Object>} - A promise that resolves to the keywords for the given GUID.
 */
const getKeywordsByGuid = async (token, guid) => {
    const method = "GET";
    const path = `${endpoint}/${guid}`;
    const headers = { Authorization: `Bearer ${token}` };
    const payload = undefined;
    return await makeRequest(method, base_url, path, headers, payload);
};

/**
 * Delete a specific keyword associated with a GUID.
 * @param {string} token - The Bearer token for authorization.
 * @param {string} guid - The UUID associated with the resource.
 * @param {Object} payload - The request payload.
 * @param {string} payload.keyword - The keyword to be deleted.
 * @returns {Promise<Object>} - A promise that resolves to the API response.
 */
const deleteKeyword = async (token, guid, payload) => {
    const method = "DELETE";
    const path = `${endpoint}/${guid}`;
    const headers = { Authorization: `Bearer ${token}` };
    return await makeRequest(method, base_url, path, headers, payload);
};

/**
 * Update a keyword for a specific GUID.
 * @param {string} token - The Bearer token for authorization.
 * @param {string} guid - The UUID associated with the resource.
 * @param {Object} payload - The request payload.
 * @param {Object} payload.keyword - The keyword object containing old and new values.
 * @param {string} payload.keyword.old - The existing keyword value.
 * @param {string} payload.keyword.new - The new keyword value.
 * @returns {Promise<Object>} - A promise that resolves to the updated resource response.
 */
const updateKeyword = async (token, guid, payload) => {
    const method = "PUT";
    const path = `${endpoint}/${guid}`;
    const headers = { Authorization: `Bearer ${token}` };
    return await makeRequest(method, base_url, path, headers, payload);
};

/**
 * Search keywords.
 * @param {string} token - The Bearer token for authorization.
 * @param {string} term - The Search Term
 * @returns {Promise<Object>} - A promise that resolves to the list of all keywords.
 */
const searchKeywords = async (token, term) => {
    const method = "GET";
    let path = `${endpoint}/search`;
    if (term) {
        const query = qs.stringify({ term }, { encodeValuesOnly: true })
        path = `${path}?${query}`
    }
    const headers = { Authorization: `Bearer ${token}` };
    const payload = undefined;
    return await makeRequest(method, base_url, path, headers, payload);
};

export {
    createKeyword,
    getAllKeywords,
    getKeywordsByGuid,
    deleteKeyword,
    updateKeyword,
    searchKeywords
};
